/**
 * Project a realtime event onto the React Query cache. [milestone M5-T6]
 *
 * One projector per event type, dispatched by `projectEvent`. The projectors
 * MUTATE the cache via `queryClient.setQueryData` (or `removeQueries` for
 * lock-release); they never call `invalidateQueries` for the MESSAGES caches
 * — invalidate triggers a refetch and that erases the optimistic patches
 * `useSendMessage` / `useClaimConversation` rely on. Conversation LISTS
 * carry no optimistic placeholders, so they MAY be invalidated — but only on
 * rare deliberate flag flips (`projectConversationUpdated`'s ordering path)
 * or through the per-key 2s throttle-with-trailing guard
 * (`invalidateListCoalesced`) that keeps an event burst from
 * refetch-storming while still landing ONE deferred refetch at the window
 * end for whatever the burst suppressed.
 *
 * LAYERING EXCEPTION — the ONE REST side effect in this module: the
 * projector is otherwise pure cache-writes, but `markViewedConversationRead`
 * fires a leading+trailing debounced fire-and-forget
 * `POST /conversations/{id}/read` when an
 * INBOUND message lands on the conversation the operator is LOOKING AT
 * (selected row or open merged thread). It lives here because the projector
 * is the single place that knows `isViewing` at event time. Without it the
 * local unread bump is suppressed but the SERVER keeps incrementing
 * `unread_count` (nothing else marks it read while viewing — the
 * ConversationRow effect is gated on the CACHED row's attention flag, which
 * stays false), and every 30s poll imports that lie: the viewed row jumps
 * into the unread zone with a phantom badge, the row effect fires
 * `POST /read`, the invalidation snaps it back — two reshuffles per poll
 * cycle during any active chat (the "ghost bounce").
 *
 * The "one true merge path" for the messages cache is `patchCacheWith` from
 * `@/features/chat/useSendMessage` — it handles BOTH "match an optimistic
 * outbound by client_ref" AND "prepend a brand-new inbound". The WS
 * `message.new` handler is two lines around it.
 */

import type { QueryClient, QueryKey } from '@tanstack/react-query';

import {
  isArchivedAccount,
  type Account,
  type AccountAttention,
} from '@/features/accounts/accounts.api';
import { accountsQueryKey } from '@/features/accounts/useAccounts';
import { type Message, type PagedMessages } from '@/features/chat/chat.api';
import { mergeDelivery } from '@/features/chat/delivery-status';
import { chatKeys } from '@/features/chat/keys';
import { patchMessageReactions } from '@/features/chat/useReactToMessage';
import {
  patchCacheWith,
  type InfiniteMessages,
} from '@/features/chat/useSendMessage';
import {
  CUCI_TAG,
  SPECIAL_TAG,
  markConversationRead,
  type Conversation,
  type Lock,
  type PagedConversations,
} from '@/features/conversations/conversations.api';
import { groupKeyOf } from '@/features/conversations/group-runs';
import {
  CONVERSATIONS_ROOT,
  conversationsKeys,
  type ConversationsListKeyParams,
} from '@/features/conversations/keys';
import type { RealtimeEvent } from '@/lib/realtime/types';
import type { ThreadSelection } from '@/store/ui';

type MessageStatusPayload = Extract<
  RealtimeEvent,
  { type: 'message.status' }
>['payload'];

type AccountStatusPayload = Extract<
  RealtimeEvent,
  { type: 'account.status' }
>['payload'];

/** Mirror of the backend's `_PREVIEW_PLACEHOLDERS` map in
 *  `hub/app/api/conversations.py`. Kept in lockstep so a WS `message.new`
 *  patches the conversations-list `last_message_preview` to exactly the
 *  string the next REST refetch would carry. */
const PREVIEW_PLACEHOLDERS: Record<string, string> = {
  image: '[Image]',
  audio: '[Voice]',
  video: '[Video]',
  document: '[File]',
  sticker: '[Sticker]',
  location: '[Location]',
  contact: '[Contact]',
  system: '[System]',
};

/** Compute the display preview for a newly-arrived message — mirror of the
 *  backend's `_message_preview(content_text, content_type)`. */
function messagePreview(message: Message): string {
  if (message.content_text) {
    return message.content_text;
  }
  return PREVIEW_PLACEHOLDERS[message.content_type] ?? '[Message]';
}

/** Shape stored in react-query for the conversations infinite list. */
type InfiniteConversations = {
  pages: PagedConversations[];
  pageParams: unknown[];
};

/** Remove the given row ids from every page (order otherwise preserved). */
function stripRows(
  pages: PagedConversations[],
  ids: ReadonlySet<string>,
): PagedConversations[] {
  return pages.map((page) => ({
    ...page,
    items: page.items.filter((c) => !ids.has(c.id)),
  }));
}

/** Key-shape predicate for a conversations-list query —
 *  `['conversations', 'list', params]`. Shared by every list lookup below
 *  (`getConversationsListQueries`, `findRowInLists`) so the projectors and
 *  the auto-read reconciliation can never drift on what counts as
 *  "a list". */
function isConversationsListKey(key: QueryKey): boolean {
  if (!Array.isArray(key) || key.length < 3) return false;
  return key[0] === CONVERSATIONS_ROOT && key[1] === 'list';
}

/** Every cached conversations-list query — `['conversations', 'list',
 *  params]` — as `[key, data]` pairs. Shared by the `message.new` and
 *  `conversation.updated` projectors and the auto-read reconciliation so
 *  the three can never drift on what counts as "a list". */
function getConversationsListQueries(
  queryClient: QueryClient,
): Array<[QueryKey, InfiniteConversations | undefined]> {
  return queryClient.getQueriesData<InfiniteConversations>({
    predicate: (query) => isConversationsListKey(query.queryKey),
  });
}

/** FRESHEST cached copy of `conversationId` across the conversations-list
 *  caches (`undefined` = cached nowhere). The honest invariant: the
 *  projector patches every cached copy identically, but REFETCHES update
 *  the copies at different times — `resetRealtimeFedCaches` invalidates
 *  with react-query's default refetchType 'active', so after a replay gap
 *  an UNMOUNTED list keeps its pre-gap rows while the mounted one
 *  refetches. Picking "any copy" (`getQueriesData` iterates creation
 *  order — oldest query first) could build the updated row from a rotten
 *  abandoned copy, and the present-row hoists would then converge every
 *  list to the stalest flags. So: the copy from the matching query with
 *  the MAX `state.dataUpdatedAt` wins. */
function findRowInLists(
  queryClient: QueryClient,
  conversationId: string,
): Conversation | undefined {
  // `getQueryCache().findAll` (not `getQueriesData`) — we need each query's
  // `state.dataUpdatedAt` alongside its data. Same key-shape predicate as
  // `getConversationsListQueries`.
  const queries = queryClient.getQueryCache().findAll({
    predicate: (query) => isConversationsListKey(query.queryKey),
  });
  let best: Conversation | undefined;
  let bestUpdatedAt = -1;
  for (const query of queries) {
    if (query.state.dataUpdatedAt <= bestUpdatedAt) continue;
    const data = query.state.data as InfiniteConversations | undefined;
    if (!data) continue;
    for (const page of data.pages) {
      const hit = page.items.find((c) => c.id === conversationId);
      if (hit !== undefined) {
        best = hit;
        bestUpdatedAt = query.state.dataUpdatedAt;
        break;
      }
    }
  }
  return best;
}

// --- Ordering predicates (mirror the backend's list ORDER BY) --------------

/** "Unread" in ordering/filter semantics is ALWAYS this disjunction — a real
 *  unread counter OR the manual attention flag. Mirrors the backend's
 *  `unread_count > 0 OR marked_unread` (both the `view=unread_pin` WHERE and
 *  the default ordering's unread zone). */
function isUnread(row: Conversation): boolean {
  return row.unread_count > 0 || row.marked_unread;
}

/** Tagged 要cuci — the zone-0 / `view=cuci` membership predicate. */
function isCuci(row: Conversation): boolean {
  return row.tags.includes(CUCI_TAG);
}

/** Tagged Special — the `view=special` membership predicate. Unlike cuci,
 *  Special has NO zone of its own in the "All" ordering (the four zones
 *  below are unchanged); the tag only drives the `view=special` filter's
 *  WHERE, so this predicate feeds membership gates but never `zoneOfRow`. */
function isSpecial(row: Conversation): boolean {
  return row.tags.includes(SPECIAL_TAG);
}

/**
 * The server's DEFAULT ("All" tab) ordering is four zones, in priority
 * order — this enum mirrors that ORDER BY (see the backend's
 * `list_conversations`):
 *
 *   0 = cuci (tagged 要cuci; pinned-cuci rows float to the zone top)
 *   1 = pinned (and not cuci)
 *   2 = unread (`unread_count > 0 OR marked_unread`) and not cuci/pinned
 *   3 = everything else (read)
 *
 * Within a zone: `pinned_at DESC NULLS LAST` orders the pinned rows,
 * everything else by `last_message_at DESC`.
 */
type ConversationZone = 0 | 1 | 2 | 3;

/** Row-level zone — ungrouped lists, and PINNED rows of grouped lists (a
 *  pinned row never takes its group's aggregated zone; it leaves the
 *  unpinned block behind — same-key PINNED rows merge among themselves via
 *  the grouped pin anchor, but their zone is still row-level). */
function zoneOfRow(row: Conversation): ConversationZone {
  if (isCuci(row)) return 0;
  if (row.pinned_at !== null) return 1;
  if (isUnread(row)) return 2;
  return 3;
}

/** Aggregated flags of a same-phone group's UNPINNED members — the grouped
 *  default ordering promotes the WHOLE block by any member's flag (the
 *  server aggregates with `FILTER (WHERE pinned_at IS NULL)`, same as the
 *  recency window MAX). */
interface GroupZoneFlags {
  cuci: boolean;
  unread: boolean;
}

/** One `groupKeyOf -> flags` map per list pass (NOT per row — a per-row scan
 *  would be O(n²) over the cached pages). Only unpinned rows contribute. */
function buildGroupZoneFlags(
  pages: PagedConversations[],
): Map<string, GroupZoneFlags> {
  const map = new Map<string, GroupZoneFlags>();
  for (const page of pages) {
    for (const row of page.items) {
      if (row.pinned_at !== null) continue;
      const key = groupKeyOf(row);
      const flags = map.get(key) ?? { cuci: false, unread: false };
      flags.cuci = flags.cuci || isCuci(row);
      flags.unread = flags.unread || isUnread(row);
      map.set(key, flags);
    }
  }
  return map;
}

/** GROUP-level zone for a grouped list's row: pinned rows stay row-level
 *  (cuci-pinned = zone 0, plain pinned = zone 1); an unpinned row takes its
 *  whole group's aggregated zone. A group only partially in the cache is
 *  judged on what the cache holds — the 30s poll reconciles the drift. */
function zoneOfGroupedRow(
  row: Conversation,
  groupFlags: Map<string, GroupZoneFlags>,
): ConversationZone {
  if (row.pinned_at !== null) {
    return isCuci(row) ? 0 : 1;
  }
  const flags = groupFlags.get(groupKeyOf(row));
  if (flags?.cuci) return 0;
  if (flags?.unread) return 2;
  return 3;
}

// --- Hoist / insert strategies ----------------------------------------------

/** Patch the updated row's FIELDS wherever it sits, moving nothing. Used for
 *  every PINNED updated row: a new message never reorders the pinned zone
 *  (`pinned_at DESC`) — in the classic ordering AND in every zone. */
function patchRowInPlace(
  data: InfiniteConversations,
  updated: Conversation,
): InfiniteConversations {
  const pages = data.pages.map((page) => {
    const idx = page.items.findIndex((c) => c.id === updated.id);
    if (idx === -1) return page;
    const items = page.items.slice();
    items[idx] = updated;
    return { ...page, items };
  });
  return { ...data, pages };
}

/** Gather the updated row's same-phone block: the updated row first, then
 *  every UNPINNED cached row sharing its `phone_e164 ?? peer_id` key, in
 *  existing cache order. Pinned rows never join a RECENCY block (the
 *  server's aggregates exclude them — `FILTER (WHERE pinned_at IS NULL)` —
 *  and `collapseGroupRuns` never fuses a pinned row with an unpinned one).
 *  A fully-pinned same-key run DOES render merged now (grouped pin anchor,
 *  feat/merged-pin) but its position is pin-time based and never moves on
 *  recency, so excluding pinned rows here stays correct. Rows not in the
 *  loaded cache are unknowable here; the next refetch reconciles. */
function collectGroupBlock(
  pages: PagedConversations[],
  updated: Conversation,
): Conversation[] {
  const key = groupKeyOf(updated);
  const siblings: Conversation[] = [];
  for (const page of pages) {
    for (const row of page.items) {
      if (row.id === updated.id || row.pinned_at !== null) continue;
      if (groupKeyOf(row) === key) siblings.push(row);
    }
  }
  return [updated, ...siblings];
}

/**
 * CLASSIC-ordering insert (lists with a `view=` filter — `unread_pin` /
 * `cuci` / `special` keep the old pinned-first-then-recency ORDER BY):
 * strip the block's rows everywhere, then insert the block immediately
 * after page-0's leading pinned prefix (pinned rows in their existing cache
 * order). Inserting below the prefix — not at absolute index 0 — is what
 * keeps a fresh unpinned row from jumping above pinned rows (the old
 * `hoistRowToTop` bug).
 */
function insertBelowPinnedPrefix(
  data: InfiniteConversations,
  block: Conversation[],
): InfiniteConversations {
  const pages = stripRows(data.pages, new Set(block.map((c) => c.id)));
  const head = pages[0].items;
  let insertAt = 0;
  while (insertAt < head.length && head[insertAt].pinned_at !== null) {
    insertAt += 1;
  }
  const items = head.slice();
  items.splice(insertAt, 0, ...block);
  pages[0] = { ...pages[0], items };
  return { ...data, pages };
}

/**
 * Hoist strategy for a `group=phone` list under the CLASSIC ordering: move
 * the ENTIRE same-phone block — the updated row plus every UNPINNED cached
 * row sharing its key — as one unit to just below page-0's leading pinned
 * prefix, updated row first (matches the server's grouped ORDER BY: pins
 * first, then whole groups by freshest unpinned member, freshest row first
 * within the group). Hoisting only the single row would split the run,
 * breaking the grouped view's adjacency promise until the next refetch.
 */
function hoistGroupBlock(
  data: InfiniteConversations,
  updated: Conversation,
): InfiniteConversations {
  return insertBelowPinnedPrefix(data, collectGroupBlock(data.pages, updated));
}

/**
 * ZONE-ordering insert (lists WITHOUT a `view=` filter — the "All" tab —
 * whose server ordering is the four-zone sort; see `ConversationZone`):
 * strip the block's rows everywhere, compute each remaining row's zone
 * (row-level ungrouped, group-level for a `group=phone` list), then insert
 * the block in page 0 before the FIRST row R where
 *
 *   zoneOf(R) > zone,  OR  (zoneOf(R) === zone AND R.pinned_at === null)
 *
 * — i.e. at the top of its own zone but BELOW that zone's pinned rows
 * (`pinned_at DESC` floats pinned-cuci rows above unpinned-cuci ones; the
 * same rule is a no-op for zones 2/3, which cannot contain pins). This
 * mirrors the backend's zone ORDER BY; any drift a partial cache can't see
 * is reconciled by the 30s poll.
 */
function insertIntoZone(
  data: InfiniteConversations,
  block: Conversation[],
  zone: ConversationZone,
  grouped: boolean,
): InfiniteConversations {
  const pages = stripRows(data.pages, new Set(block.map((c) => c.id)));
  // Group zones are judged on what REMAINS cached after the strip — the
  // block's own (stale) rows must not feed other rows' zones. One map per
  // pass; `zoneOfGroupedRow` is then O(1) per row.
  const groupFlags = grouped ? buildGroupZoneFlags(pages) : null;
  const zoneOf = (row: Conversation): ConversationZone =>
    groupFlags !== null ? zoneOfGroupedRow(row, groupFlags) : zoneOfRow(row);

  const head = pages[0].items;
  let insertAt = 0;
  while (insertAt < head.length) {
    const rowZone = zoneOf(head[insertAt]);
    if (
      rowZone > zone ||
      (rowZone === zone && head[insertAt].pinned_at === null)
    ) {
      break;
    }
    insertAt += 1;
  }
  const items = head.slice();
  items.splice(insertAt, 0, ...block);
  pages[0] = { ...pages[0], items };
  return { ...data, pages };
}

// --- Coalesced list invalidation --------------------------------------------

/** Repeat invalidations of the SAME key inside this window are deferred
 *  (never fired immediately): react-query RESTARTS an in-flight refetch
 *  when the key is invalidated again (`cancelRefetch` defaults to true), so
 *  an event burst would otherwise cancel every GET before it completes — a
 *  refetch storm that never lands. */
const INVALIDATION_COALESCE_MS = 2_000;

/** serialized queryKey -> epoch ms of the last invalidation (leading OR
 *  trailing). Module-level for the same reason as `SEEN_SEQS` (the
 *  projector is not a component). Cardinality is bounded by the set of
 *  cached list keys (plus the `lists()` root), so no trimming is needed.
 *  Tests reset it via `__testing.resetInvalidationCoalesce`. */
const LAST_INVALIDATED_AT = new Map<string, number>();

/** serialized queryKey -> pending TRAILING invalidation timer (at most one
 *  per key). See `invalidateListCoalesced`; cancelled alongside the
 *  timestamp ledger by `__testing.resetInvalidationCoalesce`. */
const PENDING_TRAILING_INVALIDATION = new Map<
  string,
  ReturnType<typeof setTimeout>
>();

/** Invalidate `queryKey`, THROTTLED WITH TRAILING: at most one immediate
 *  invalidation per key per `INVALIDATION_COALESCE_MS`, and a call
 *  suppressed inside the window is NOT dropped — dropping it forever would
 *  let conv A's invalidation at T0 swallow conv B's (different conversation,
 *  same list key) at T0+1s, parking the mounted attention tab on the 30s
 *  poll for B's brand-new work. Instead ONE deferred invalidation is
 *  scheduled for the window end (at most one pending timer per key); the
 *  timestamp is stamped when the trailing call actually FIRES, so a
 *  continuing burst keeps throttling off the trailing refetch. Used by the
 *  projector's "row cached NOWHERE" paths, where a refetch is the only way
 *  to surface new membership. */
function invalidateListCoalesced(
  queryClient: QueryClient,
  queryKey: QueryKey,
): void {
  const serialized = JSON.stringify(queryKey);
  const now = Date.now();
  const last = LAST_INVALIDATED_AT.get(serialized);
  if (last !== undefined && now - last < INVALIDATION_COALESCE_MS) {
    // Suppressed inside the window — defer, don't drop. One pending
    // trailing timer per key; a second suppressed call rides the same one.
    if (!PENDING_TRAILING_INVALIDATION.has(serialized)) {
      const remaining = INVALIDATION_COALESCE_MS - (now - last);
      const timer = setTimeout(() => {
        PENDING_TRAILING_INVALIDATION.delete(serialized);
        LAST_INVALIDATED_AT.set(serialized, Date.now());
        void queryClient.invalidateQueries({ queryKey });
      }, remaining);
      PENDING_TRAILING_INVALIDATION.set(serialized, timer);
    }
    return;
  }
  LAST_INVALIDATED_AT.set(serialized, now);
  void queryClient.invalidateQueries({ queryKey });
}

// --- Viewed-conversation auto-read (the projector's ONE REST side effect) ---

/** Debounce window for the auto-read POST — a burst of inbound messages
 *  while the operator watches the chat collapses into a LEADING `POST
 *  /read` plus, when the burst continues inside the window, ONE TRAILING
 *  POST at the window end. Leading-only would re-create the very ghost
 *  bounce this side effect exists to kill: msg2 arriving 1s after msg1's
 *  POST is counted by the server but never cleared (ingest publishes no
 *  `conversation.updated`), and the next 30s poll imports the lie — and a
 *  WhatsApp burst routinely ends <2s after the last POST. */
const VIEWED_READ_DEBOUNCE_MS = 2_000;

/** conversation id -> epoch ms of the last auto-read POST (leading OR
 *  trailing). Bounded in practice by the set of conversations an operator
 *  actually views in one session. Tests reset it via
 *  `__testing.resetViewedReadDebounce`. */
const VIEWED_READ_LAST_POST = new Map<string, number>();

/** conversation id -> pending TRAILING auto-read timer (at most one per
 *  conversation). Cancelled alongside the timestamp ledger by
 *  `__testing.resetViewedReadDebounce`. */
const VIEWED_READ_PENDING_TRAILING = new Map<
  string,
  ReturnType<typeof setTimeout>
>();

/**
 * The actual fire-and-forget `POST /conversations/{id}/read`, shared by the
 * leading call and the trailing timer (the trailing call re-checks nothing —
 * it just POSTs: the endpoint is idempotent and a redundant read-reset is
 * harmless).
 *
 * On success, ONLY `unread_count` + `marked_unread` are merged onto the
 * EXISTING cached copy in each list and in the detail slot. The HTTP
 * response is a POST-time snapshot travelling on a DIFFERENT, UNORDERED
 * channel from the WS events: by the time it resolves, a later WS event may
 * already have advanced preview / last_message_at / pin / tags in the
 * cache, and blind-writing the whole snapshot would regress them (stale
 * last-writer-wins). The response's only authoritative payload is the
 * read-reset, so that is all we take. Caches that do not hold the row are
 * SKIPPED — no insert (membership is the projectors' job; resurrecting a
 * row that left a view during the RTT would be wrong). Errors are swallowed
 * with a console.warn — the endpoint is idempotent and the 30s poll
 * self-heals.
 */
function postViewedConversationRead(
  queryClient: QueryClient,
  conversationId: string,
): void {
  markConversationRead(conversationId)
    .then((conversation) => {
      const detailKey = conversationsKeys.detail(conversationId);
      const cachedDetail = queryClient.getQueryData<Conversation>(detailKey);
      if (cachedDetail !== undefined) {
        queryClient.setQueryData<Conversation>(detailKey, {
          ...cachedDetail,
          unread_count: conversation.unread_count,
          marked_unread: conversation.marked_unread,
        });
      }
      for (const [key, data] of getConversationsListQueries(queryClient)) {
        if (!data) continue;
        const cached = data.pages
          .flatMap((page) => page.items)
          .find((c) => c.id === conversationId);
        if (cached === undefined) continue;
        // Merged row = the CACHED copy's fields + the read-reset only.
        queryClient.setQueryData(
          key,
          patchRowInPlace(data, {
            ...cached,
            unread_count: conversation.unread_count,
            marked_unread: conversation.marked_unread,
          }),
        );
      }
    })
    .catch((error: unknown) => {
      // Low stakes: POST /read is idempotent and the 30s poll re-imports
      // server truth either way. Log so a systematic failure stays visible.
      console.warn(
        '[realtime] auto mark-read of viewed conversation failed',
        error,
      );
    });
}

/**
 * Leading+trailing debounced `POST /conversations/{id}/read` for the
 * conversation the operator is currently VIEWING when an inbound message
 * lands — see the module doc's LAYERING EXCEPTION for why this REST call
 * lives in the projector (it is the one place that knows `isViewing` at
 * event time; without it the server's `unread_count` keeps climbing while
 * the local bump is suppressed, and the 30s poll ghost-bounces the row).
 *
 * LEADING: the first inbound POSTs immediately. TRAILING: a call suppressed
 * inside the window schedules ONE deferred POST for the window end (at most
 * one pending timer per conversation; later suppressed calls ride the same
 * timer). The trailing fire re-stamps the ledger, so a continuing burst
 * keeps chaining windows — every burst is guaranteed to END with a POST,
 * which is what keeps the server's `unread_count` at 0 when the burst goes
 * quiet.
 *
 * Uses the raw API fn (NOT the `useReadConversation` hook — this is not a
 * component) and needs no optimistic walk: the local caches already show
 * the row as read because the projector suppressed the bump. The POST +
 * success/error handling live in `postViewedConversationRead`.
 */
function markViewedConversationRead(
  queryClient: QueryClient,
  conversationId: string,
): void {
  const now = Date.now();
  const last = VIEWED_READ_LAST_POST.get(conversationId);
  if (last !== undefined && now - last < VIEWED_READ_DEBOUNCE_MS) {
    // Inside the window — defer to the window end instead of dropping.
    if (!VIEWED_READ_PENDING_TRAILING.has(conversationId)) {
      const remaining = VIEWED_READ_DEBOUNCE_MS - (now - last);
      const timer = setTimeout(() => {
        VIEWED_READ_PENDING_TRAILING.delete(conversationId);
        VIEWED_READ_LAST_POST.set(conversationId, Date.now());
        postViewedConversationRead(queryClient, conversationId);
      }, remaining);
      VIEWED_READ_PENDING_TRAILING.set(conversationId, timer);
    }
    return;
  }
  VIEWED_READ_LAST_POST.set(conversationId, now);
  postViewedConversationRead(queryClient, conversationId);
}

/**
 * Patch every cached conversations list whose scope matches the message's
 * conversation. Bumps `last_message_preview`, `last_message_at`, and (for
 * inbound + not-currently-viewing) `unread_count`.
 *
 * The updated row is built ONCE from the FRESHEST cached copy found across
 * the lists (see `findRowInLists` — the projector patches copies
 * identically, but refetches update them at different times, e.g. after a
 * replay gap only the ACTIVE lists refetch, so max `dataUpdatedAt` wins),
 * then applied per list.
 *
 * Reordering depends on the list's ORDERING MODE, read off the key's params
 * object (`params.view`, `params.group` — see `keys.ts`):
 *
 *   - `view` set (`unread_pin` / `cuci` / `special`): the server keeps the
 *     CLASSIC ordering (pinned first, then recency / grouped recency).
 *     Unpinned rows hoist below the pinned prefix (whole block in
 *     `group=phone` mode).
 *   - no `view` (the "All" tab): the server orders by four zones (cuci →
 *     pinned → unread → read; Special has no zone); the block is inserted
 *     at the top of its own zone — see `insertIntoZone`.
 *   - a PINNED row already present is patched in place in EVERY mode:
 *     recency never reorders the pinned zone (`pinned_at DESC`; the grouped
 *     PIN ANCHOR is pin-time based too, so a fully-pinned merged block
 *     never moves on a message either).
 *
 * MEMBERSHIP IS PROJECTED TOO (feat/conversations-filter-redesign — this
 * used to be "membership is never projected", which parked an operator on
 * the Unread & Pin tab up to 30s behind NEW incoming work — the attention
 * queue's core event). Per list:
 *
 *   - Row PRESENT in the list -> mode-aware hoist, exactly as before.
 *   - Row ABSENT from the list but cached in another one -> membership is
 *     decided on the POST-update fields: account scope first (an insert
 *     cannot rely on the old "row present ⇒ account matches" shortcut),
 *     then the view's own predicate — no-view "All" always belongs;
 *     `unread_pin` iff pinned OR unread (an inbound bump when not viewing
 *     always qualifies; when viewing, the row may legitimately still be
 *     read and is correctly skipped); `cuci` / `special` iff carrying the
 *     matching tag. Members are INSERTED via the same mode-aware paths
 *     (`stripRows` no-ops on an absent row); non-members are skipped.
 *   - Row cached NOWHERE -> there is no row object to insert. For an
 *     inbound-and-not-viewing bump (membership in `unread_pin` is then
 *     guaranteed) every matching cached `view=unread_pin` list AND every
 *     matching cached no-view "All" list is INVALIDATED, throttled to at
 *     most one immediate refetch per key per 2s with a trailing deferral
 *     (see `invalidateListCoalesced` — an inbound burst for uncached
 *     conversations must not refetch-storm). The All lists are included
 *     because a FIRST-EVER message from a new customer is cached nowhere
 *     yet belongs at the top of All's unread zone on page 0 — it must not
 *     wait for the 30s poll. Tag-filtered lists (cuci / special) are
 *     deliberately NOT invalidated on this path: a message cannot change
 *     tag membership.
 *
 * A row LEAVING a view is still only removed by a refetch.
 */
function projectMessageNewIntoLists(
  queryClient: QueryClient,
  message: Message,
  isInboundAndNotViewing: boolean,
): void {
  const matchingQueries = getConversationsListQueries(queryClient);

  // Find the row's FRESHEST cached copy first (max dataUpdatedAt across the
  // lists — see findRowInLists) — the single source the per-list pass below
  // patches/inserts from.
  const globalExisting = findRowInLists(queryClient, message.conversation_id);

  // Row cached NOWHERE: nothing to patch and no row object to insert. An
  // inbound-not-viewing bump still guarantees unread_pin membership, and a
  // FIRST-EVER conversation belongs at the top of the All tab's unread zone
  // — both must refetch (throttled with trailing) instead of waiting 30s.
  // Tag-filtered lists (cuci / special) are skipped: a message cannot
  // change tag membership.
  if (globalExisting === undefined) {
    if (!isInboundAndNotViewing) return;
    for (const [key] of matchingQueries) {
      // The list-query predicate guaranteed key[2] is the params object.
      const params = key[2] as ConversationsListKeyParams;
      if (params.view !== 'unread_pin' && params.view !== undefined) continue;
      // Scope: skip other accounts' lists when the message knows its
      // account (`account_id` is optional on Message — absent means we
      // can't tell, so invalidate rather than miss work).
      if (
        params.accountId !== null &&
        message.account_id !== undefined &&
        params.accountId !== message.account_id
      ) {
        continue;
      }
      invalidateListCoalesced(queryClient, key);
    }
    return;
  }

  // Build the updated row ONCE (same field bumps as always).
  const updated: Conversation = {
    ...globalExisting,
    last_message_preview: messagePreview(message),
    last_message_at: message.created_at,
    unread_count: isInboundAndNotViewing
      ? globalExisting.unread_count + 1
      : globalExisting.unread_count,
  };

  for (const [key, data] of matchingQueries) {
    if (!data || data.pages.length === 0) continue;

    // The list-query predicate guaranteed key[2] is the params object.
    const params = key[2] as ConversationsListKeyParams;
    const grouped = params.group === 'phone';
    const present = data.pages.some((page) =>
      page.items.some((c) => c.id === updated.id),
    );

    if (!present) {
      // Membership gate for the insert path — POST-update fields decide.
      if (params.accountId !== null && params.accountId !== updated.account_id) {
        continue;
      }
      if (params.view === 'unread_pin') {
        // Mirror of the server WHERE: pinned OR unread.
        if (updated.pinned_at === null && !isUnread(updated)) continue;
      } else if (params.view === 'cuci') {
        if (!isCuci(updated)) continue;
      } else if (params.view === 'special') {
        if (!isSpecial(updated)) continue;
      }
      // No `view` (the "All" tab): every row in the account scope belongs.
    }

    let next: InfiniteConversations;
    if (updated.pinned_at !== null) {
      // Pinned rows never move on a message — both ordering modes sort the
      // pinned zone by `pinned_at DESC`, which a message does not change
      // (the grouped PIN ANCHOR is pin-time based too, so a fully-pinned
      // merged block holds its slot as well — the merged ROW's fields still
      // refresh because its members are patched in place here).
      // Present -> in-place field patch. Absent (a pinned row newly entering
      // this list's cache) -> single-row insert: a pinned row never joins
      // the unpinned recency block in any mode, and both insert paths land
      // the row at the tail of its pinned run (the row itself is pinned, so
      // the prefix/zone invariant holds — the exact `pinned_at DESC` /
      // pin-anchor slot is the next refetch's job).
      next = present
        ? patchRowInPlace(data, updated)
        : params.view !== undefined
          ? insertBelowPinnedPrefix(data, [updated])
          : insertIntoZone(data, [updated], zoneOfRow(updated), grouped);
    } else if (params.view !== undefined) {
      // view=unread_pin / cuci / special — CLASSIC ordering (pins, then
      // recency).
      next = grouped
        ? hoistGroupBlock(data, updated)
        : insertBelowPinnedPrefix(data, [updated]);
    } else if (grouped) {
      // "All" tab, group=phone — the BLOCK takes the zone of its members'
      // aggregated (post-update) flags: any cuci → 0, any unread → 2, else 3.
      // Zone 1 is impossible: every block member is unpinned by construction.
      const block = collectGroupBlock(data.pages, updated);
      const zone: ConversationZone = block.some(isCuci)
        ? 0
        : block.some(isUnread)
          ? 2
          : 3;
      next = insertIntoZone(data, block, zone, true);
    } else {
      // "All" tab, ungrouped — row-level zone from the POST-update fields
      // (the unread bump above may have just moved it from zone 3 to 2).
      next = insertIntoZone(data, [updated], zoneOfRow(updated), false);
    }
    queryClient.setQueryData(key, next);
  }
}

/**
 * Patch ONE message, by id, inside ONE messages-cache slot — a
 * per-conversation window (`chatKeys.messages`) or a merged-thread window
 * (`chatKeys.threadMessages`); both hold the `InfiniteMessages` envelope.
 * The row keeps its array index and its `client_ref` (the React key on
 * `MessageBubble`), so nothing remounts.
 *
 * Writes NOTHING when the slot is not cached or does not hold the id (so a
 * fan-out over every thread slot never touches unrelated windows). When it
 * does write, it preserves the slot's freshness clock (same discipline as
 * `patchCacheWith`): a patch is an incremental write, not a full sync —
 * re-stamping `dataUpdatedAt = now` would mark a GC'd-then-rebuilt partial
 * cache "fresh" again and suppress the mount refetch that restores the
 * missing history.
 */
function patchMessageInSlot(
  queryClient: QueryClient,
  key: QueryKey,
  messageId: string,
  patch: (message: Message) => Message,
): void {
  const state = queryClient.getQueryState<InfiniteMessages>(key);
  const old = state?.data;
  if (!state || !old) return;
  let patched = false;
  const pages = old.pages.map((page) => {
    if (patched) return page;
    const idx = page.items.findIndex((m) => m.id === messageId);
    if (idx === -1) return page;
    patched = true;
    const items = page.items.slice();
    items[idx] = patch(items[idx]);
    return { ...page, items };
  });
  if (!patched) return;
  queryClient.setQueryData<InfiniteMessages>(
    key,
    { ...old, pages },
    { updatedAt: state.dataUpdatedAt },
  );
}

/** Every cached merged-thread window (`['chat', 'thread-messages', key]`),
 *  open or not — a closed thread's slot lingers until GC and is shown again
 *  instantly if the operator reopens it, so it must not go stale either. */
function cachedThreadSlotKeys(queryClient: QueryClient): QueryKey[] {
  return queryClient
    .getQueryCache()
    .findAll({ queryKey: chatKeys.threadMessagesRoot() })
    .map((query) => query.queryKey);
}

/** Apply `patch` to message `messageId` in the conversation's own window
 *  AND in every cached merged-thread window holding it. The merged view
 *  reads ONLY its thread slot, so a patch that reached just the
 *  per-conversation window was invisible there (spec 00 E6 / 02 §2 P6). */
function patchMessageEverywhere(
  queryClient: QueryClient,
  conversationId: string,
  messageId: string,
  patch: (message: Message) => Message,
): void {
  patchMessageInSlot(queryClient, chatKeys.messages(conversationId), messageId, patch);
  for (const key of cachedThreadSlotKeys(queryClient)) {
    patchMessageInSlot(queryClient, key, messageId, patch);
  }
}

/** Project a `message.status` event onto every cached copy of the message
 *  (its conversation's window + every merged-thread window holding it).
 *
 *  Delivery fields go through `mergeDelivery` — MONOTONIC: a stale write
 *  (e.g. the send endpoint's `sent` published after the receipt's
 *  `delivered`) never moves a `delivered` / `read` row back down; the 0015
 *  side fields (`delivery_error`, `delivered_at`, `read_at`) follow the
 *  merge's rules (an absent key keeps the cached value, `null` clears
 *  `delivery_error`).
 *
 *  `platform_message_id` matters beyond display: it's the value a reply
 *  quotes (`reply_to_platform_message_id` on another message) and the key
 *  `MessageList` indexes the loaded window by to resolve a quoted-preview.
 *  An outbound send's POST response may return it synchronously, or (when
 *  the platform assigns the wamid only after an async gateway ack) it may
 *  only ever arrive via this event — so we backfill from `payload` when
 *  present rather than only touching the delivery fields. Only overwrite
 *  when the payload actually carries a value: a status event that doesn't
 *  know the wamid yet must not clobber one already patched in by an earlier
 *  event or the POST response. */
function projectMessageStatusIntoChat(
  queryClient: QueryClient,
  payload: MessageStatusPayload,
): void {
  patchMessageEverywhere(
    queryClient,
    payload.conversation_id,
    payload.message_id,
    (message) => ({
      ...message,
      ...mergeDelivery(message, payload),
      platform_message_id: payload.platform_message_id ?? message.platform_message_id,
    }),
  );
}

/** Nullish-tolerant equality: `undefined` (field absent — older hub or a
 *  locally-built row) and `null` (explicitly none) mean the same thing to
 *  every consumer, so flipping between them is not a change worth a
 *  re-render. */
function sameNullable<T>(a: T | null | undefined, b: T | null | undefined): boolean {
  return (a ?? null) === (b ?? null);
}

function sameAttention(
  a: AccountAttention | null | undefined,
  b: AccountAttention | null | undefined,
): boolean {
  if (a == null || b == null) return a == null && b == null;
  return (
    a.level === b.level &&
    a.reason === b.reason &&
    a.scope === b.scope &&
    a.since === b.since
  );
}

/**
 * Apply one `account.status` payload to its cached account row.
 *
 * EVERY field is patched even when `state` is unchanged: attention is
 * time-based — the hub re-publishes the SAME state once `connecting` has
 * lasted past its stuck threshold (`connecting_stuck`), and a detail-only
 * change carries the same state too. (The old projector skipped an
 * unchanged `state`, which would have hidden exactly those — spec 01 §2
 * item 4, R4.)
 *
 *   - `state` -> `status`; `detail` -> `status_detail`;
 *     `status_since` / `attention` -> the same-named fields.
 *   - A key the payload OMITS (a pre-0015 hub sends no `status_since` /
 *     `attention`) keeps the cached value; an explicit `null` clears it.
 *
 * Returns the SAME row object when nothing changed, so a no-op event causes
 * no re-render.
 */
function applyAccountStatus(row: Account, payload: AccountStatusPayload): Account {
  const statusDetail =
    payload.detail === undefined ? row.status_detail : payload.detail;
  const statusSince =
    payload.status_since === undefined ? row.status_since : payload.status_since;
  const attention =
    payload.attention === undefined ? row.attention : payload.attention;
  if (
    row.status === payload.state &&
    sameNullable(row.status_detail, statusDetail) &&
    sameNullable(row.status_since, statusSince) &&
    sameAttention(row.attention, attention)
  ) {
    return row;
  }
  return {
    ...row,
    status: payload.state,
    status_detail: statusDetail,
    status_since: statusSince,
    attention,
  };
}

/** Shallow tags-array equality (order-sensitive — the backend stores and
 *  returns the array verbatim, so a reorder IS a change worth re-sorting
 *  for; in practice v1 only ever toggles the single `CUCI_TAG`). */
function sameTags(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((tag, i) => tag === b[i]);
}

/** Did this update change anything that ORDERS a list or decides its
 *  MEMBERSHIP? The operator flags now drive both the "All" tab's four-zone
 *  ordering (cuci → pinned → unread → read) AND the `view=unread_pin` /
 *  `view=cuci` filters' WHERE clauses, so any of:
 *
 *    - `pinned_at` changed (pin/unpin — zone 1 and the pinned prefix),
 *    - `marked_unread` changed (zone 2 / unread_pin membership),
 *    - `tags` changed (zone 0 / cuci membership),
 *    - `unread_count` crossed 0 ↔ >0 (a read action, or the first unread —
 *      counts moving WITHIN >0 change neither zone nor membership)
 *
 *  means the cached row order / row set may be stale. */
function orderingRelevantChange(prev: Conversation, next: Conversation): boolean {
  return (
    prev.pinned_at !== next.pinned_at ||
    prev.marked_unread !== next.marked_unread ||
    !sameTags(prev.tags, next.tags) ||
    (prev.unread_count > 0) !== (next.unread_count > 0)
  );
}

/** Patch a single conversation row in every cached list (after a
 *  `conversation.updated` event — assign / read / flags / status change).
 *
 *  Ordering & membership: the in-place patch updates the row's FIELDS but
 *  cannot move the row between zones, nor add/remove it from a filtered
 *  `view=` list — and the ordering (four zones on the "All" tab, pinned-
 *  then-recency on the view tabs) is the SERVER's, deliberately not
 *  re-implemented client-side (a local comparator would be a second
 *  implementation that can drift). So the lists are ADDITIONALLY
 *  invalidated, in exactly two cases:
 *
 *    - the row WAS found in a cached page AND the event changed an
 *      ordering/membership-relevant field (see `orderingRelevantChange`):
 *      direct invalidation — the refetch re-asserts server order and row
 *      membership. Flag flips are rare, deliberate actions — one refetch
 *      is cheap.
 *    - the row was found in NO cached page AND the payload is
 *      ATTENTION-RELEVANT (pinned, carrying ANY business tag, marked-unread,
 *      or unread): a cross-console tag/pin on a row beyond my loaded pages
 *      must surface in my Cuci / Special / Unread & Pin tab now, not at the
 *      next 30s poll — but there is no cached prev row to diff against, so
 *      the payload's own flags decide. ANY tag counts (not just cuci): every
 *      tag is membership-relevant to some tag-filtered tab, and the tags
 *      array is open-ended. Plain read-echoes of uncached rows are all-falsy
 *      and skip. This path runs through the same 2s throttle-with-trailing
 *      guard as the message-path invalidation (`invalidateListCoalesced`),
 *      so a burst of uncached-row echoes cannot refetch-storm — suppressed
 *      calls are deferred to the window end, not dropped.
 *
 *  A cached row with NO ordering-relevant change is patched in place only —
 *  no invalidation (no refetch churn on every unread-count tick).
 *  Invalidating here is safe: conversation lists carry no optimistic
 *  placeholders that a refetch could erase (the send-message and claim-lock
 *  optimistic state live in the messages and lock caches respectively), and
 *  `useConversationFlags` itself invalidates the same key on settle anyway. */
function projectConversationUpdated(
  queryClient: QueryClient,
  next: Conversation,
): void {
  const matchingQueries = getConversationsListQueries(queryClient);
  let orderingChanged = false;
  let foundAnywhere = false;
  for (const [key, data] of matchingQueries) {
    if (!data) continue;
    let patched = false;
    const newPages = data.pages.map((page) => {
      const idx = page.items.findIndex((c) => c.id === next.id);
      if (idx === -1) return page;
      patched = true;
      if (orderingRelevantChange(page.items[idx], next)) {
        orderingChanged = true;
      }
      const items = page.items.slice();
      items[idx] = next;
      return { ...page, items };
    });
    if (patched) {
      foundAnywhere = true;
      queryClient.setQueryData(key, { ...data, pages: newPages });
    }
  }
  if (orderingChanged) {
    // Ordering / view membership changed on a cached row — let the server
    // re-sort and re-filter (see the function doc).
    void queryClient.invalidateQueries({ queryKey: conversationsKeys.lists() });
  } else if (
    !foundAnywhere &&
    (next.pinned_at !== null ||
      next.tags.length > 0 ||
      next.marked_unread ||
      next.unread_count > 0)
  ) {
    // Bedrock for rows beyond every loaded page: an attention-relevant
    // update must surface in the flag-driven tabs without waiting for the
    // 30s poll — coalesced, because uncached-row echoes can arrive in
    // bursts (see the function doc).
    invalidateListCoalesced(queryClient, conversationsKeys.lists());
  }
  // Also keep the per-conversation detail cache slot fresh (used by
  // `ChatPanel`'s header query).
  queryClient.setQueryData(conversationsKeys.detail(next.id), next);
}

/** Write the lock into / out of `['conversations', 'lock', id]`. */
function projectConversationLock(
  queryClient: QueryClient,
  payload:
    | { conversation_id: string; locked: true; lock: Lock }
    | { conversation_id: string; locked: false; released_by_agent_id: string },
): void {
  const key = conversationsKeys.lock(payload.conversation_id);
  if (payload.locked) {
    queryClient.setQueryData<Lock>(key, payload.lock);
  } else {
    // setQueryData(key, undefined) is a no-op in react-query (the cache slot
    // keeps its prior value); use removeQueries with `exact` so the row
    // immediately repaints as unlocked.
    queryClient.removeQueries({ queryKey: key, exact: true });
  }
}

/**
 * Set of `(type, seq)` pairs we have already projected this session — the
 * top-level idempotency guard.
 *
 * Why a single shared Set instead of per-cache deduplication: every realtime
 * event the Hub publishes carries a monotonic `seq` minted in Redis (see
 * `hub/app/realtime/bus.py::publish_event`). The same `(type, seq)` should
 * NEVER reach a single client more than once — but every conceivable failure
 * mode (StrictMode duplicate WS, replay-overlap on reconnect, future Redis
 * pub/sub dup, a buggy retry loop) reduces to "the SAME (type, seq) arrives
 * twice". Skipping the second visit at the dispatcher is the cheapest, most
 * complete defense.
 *
 * The set is bounded (once it grows past ~5_000 seqs the oldest third is
 * dropped, so it never holds fewer than ~3_300 — still more than the Hub's
 * `BUFFER_SIZE` replay window, raised 1000 -> 3000 in 2026-09 because
 * receipts roughly double the outbound event rate) so a long-lived session
 * does not leak memory.
 *
 * Note: presence frames also carry a seq but are intentionally still
 * dispatched even on a duplicate — they have no side effect today (the
 * branch is a no-op pending a future presence consumer) and we want the
 * `_exhaustive` discriminator check to keep narrowing the union.
 */
const SEEN_SEQS = new Set<string>();
const SEEN_SEQS_MAX = 5_000;
function rememberSeq(key: string): boolean {
  if (SEEN_SEQS.has(key)) return false;
  SEEN_SEQS.add(key);
  if (SEEN_SEQS.size > SEEN_SEQS_MAX) {
    // Trim the oldest ~1/3 of entries. Set iterates insertion-order; we
    // delete the first N. Cheap enough at 5k entries / once-per-1700-events.
    const drop = Math.floor(SEEN_SEQS_MAX / 3);
    let i = 0;
    for (const k of SEEN_SEQS) {
      if (i++ >= drop) break;
      SEEN_SEQS.delete(k);
    }
  }
  return true;
}

/**
 * Top-level dispatcher. Pure side-effect on the cache; returns nothing.
 *
 * `getSelectedConversationId` is a getter (NOT a captured value) so the
 * predicate "is the user currently viewing this conversation?" reads the
 * latest `selectedConversationId` at the moment the event arrives, not the
 * value at hook-mount time.
 *
 * Idempotency: the dispatcher skips any `(type, seq)` it has already
 * projected. This is the single load-bearing guarantee for the cache — even
 * if the same event arrives N times (StrictMode's transient double WS,
 * replay overlap on a reconnect, hypothetical Redis fan-out dup), only the
 * first visit mutates the cache. See `SEEN_SEQS`.
 *
 * @param queryClient - The shared QueryClient instance.
 * @param event - The validated event from the WebSocket.
 * @param getSelectedConversationId - Lazy read of the UI store's
 *   selectedConversationId (used to decide whether to bump unread).
 * @param getSelectedThread - Lazy read of the UI store's `selectedThread`
 *   (the open merged phone-thread, or null). Same lazy-getter rationale.
 *   Optional so pre-thread callers/tests keep working; treated as
 *   "no thread open" when absent. When a `message.new` lands for a MEMBER
 *   conversation of the open thread, the thread's messages cache is patched
 *   via the same merge path and the unread bump is suppressed (the operator
 *   is looking at that customer right now).
 */
export function projectEvent(
  queryClient: QueryClient,
  event: RealtimeEvent,
  getSelectedConversationId: () => string | null,
  getSelectedThread?: () => ThreadSelection | null,
): void {
  // Idempotency gate — see SEEN_SEQS doc above. `${type}:${seq}` is enough:
  // the Hub mints seq from a single Redis INCR shared by all event types.
  if (!rememberSeq(`${event.type}:${event.seq}`)) {
    return;
  }
  switch (event.type) {
    case 'message.new': {
      const { conversation_id, message } = event.payload;
      // The "one true merge path" for the chat cache — handles match-by-
      // client_ref AND prepend-brand-new-inbound, see useSendMessage.ts.
      patchCacheWith(queryClient, chatKeys.messages(conversation_id), message);

      // Merged phone-thread: when the message belongs to a MEMBER
      // conversation of the currently-open thread, mirror it into the
      // thread's messages cache — same merge util, second slot. A member
      // list is what the inbox knew at selection time; a brand-new
      // conversation for the same customer (not in memberIds) misses here
      // and reconciles on the thread's 30s poll.
      const thread = getSelectedThread?.() ?? null;
      const isThreadMember =
        thread !== null && thread.memberIds.includes(conversation_id);
      if (thread !== null && isThreadMember) {
        patchCacheWith(
          queryClient,
          chatKeys.threadMessages(thread.key),
          message,
        );
      }

      // Mirror the row update into the conversations list cache so the
      // sidebar shows the new preview + bumps unread for inbound msgs the
      // user isn't actively reading. "Actively reading" covers BOTH the
      // single-conversation panel AND an open merged thread that contains
      // this conversation — the thread view is the member's read surface,
      // so bumping unread there would flag messages the operator is
      // looking at (ThreadPanel's mark-read fan-out clears server truth).
      const isInbound = message.direction === 'inbound';
      const selectedId = getSelectedConversationId();
      const isViewing = selectedId === conversation_id || isThreadMember;
      if (isInbound && isViewing) {
        // The operator is LOOKING AT this conversation: the local unread
        // bump is suppressed below, but the SERVER keeps counting — tell it
        // "read" now (debounced, fire-and-forget), or the next 30s poll
        // imports a lying unread_count and ghost-bounces the viewed row
        // through the unread zone. See the module-doc LAYERING EXCEPTION.
        markViewedConversationRead(queryClient, conversation_id);
      }
      projectMessageNewIntoLists(
        queryClient,
        message,
        isInbound && !isViewing,
      );
      return;
    }

    case 'message.status': {
      projectMessageStatusIntoChat(queryClient, event.payload);
      return;
    }

    case 'message.reaction': {
      // Replace the ONE target message's `reactions` with the payload's full
      // list — and touch nothing else. A reaction is not a message: no
      // unread bump, no conversation re-sort, no preview change. Reuses the
      // same cache write path as `useReactToMessage`, so an event for our own
      // just-confirmed reaction lands on the identical shape the optimistic
      // patch/onSuccess already wrote (a visual no-op).
      const { conversation_id, message_id, reactions } = event.payload;
      patchMessageReactions(queryClient, conversation_id, message_id, reactions);
      // Same full-list replace in every cached merged-thread window holding
      // the message — the merged view reads only its thread slot, so live
      // reactions would otherwise never show there.
      for (const key of cachedThreadSlotKeys(queryClient)) {
        patchMessageInSlot(queryClient, key, message_id, (message) => ({
          ...message,
          reactions,
        }));
      }
      return;
    }

    case 'conversation.updated': {
      projectConversationUpdated(queryClient, event.payload);
      return;
    }

    case 'conversation.lock': {
      projectConversationLock(queryClient, event.payload);
      return;
    }

    case 'presence': {
      // Phase 1: no UI consumer for presence realtime events yet. Typing /
      // viewing indicators aren't surfaced in T4/T5. Land the branch so a
      // future consumer is a one-file change.
      return;
    }

    case 'account.status': {
      // M6-T3: patch the accounts cache so the sidebar's status dot, the
      // QR attention banner AND the QR pairing dialog all react live —
      // 2026-09: plus `status_detail` / `status_since` / `attention`, the
      // inputs of the early-warning banners (see `applyAccountStatus`).
      //
      // The wire payload's `account_id` is the gateway's EXTERNAL account id
      // (e.g. `wa_test_01`), not the Hub's UUID primary key — the cached
      // `Account[]` carries both, so we match on `external_account_id`.
      // The cache update is in-place (not invalidate) so the QR dialog's
      // status-watch useEffect fires on the next render without a refetch
      // round-trip.
      //
      // A row turning `connected` (or a HIDDEN row reporting `connected`)
      // also refetches the list: on that report the hub un-hides the row and
      // may auto-supersede (hide) the other dead rows of the same number —
      // `archived_at` changes the event does not carry (hub migration 0016).
      const payload = event.payload;
      let refetch = false;
      queryClient.setQueryData<Account[]>(accountsQueryKey, (current) => {
        if (!current) return current;
        let changed = false;
        const next = current.map((row) => {
          if (row.external_account_id !== payload.account_id) return row;
          if (
            payload.state === 'connected' &&
            (row.status !== 'connected' || isArchivedAccount(row))
          ) {
            refetch = true;
          }
          const patched = applyAccountStatus(row, payload);
          if (patched !== row) changed = true;
          return patched;
        });
        return changed ? next : current;
      });
      if (refetch) {
        void queryClient.invalidateQueries({ queryKey: accountsQueryKey });
      }
      return;
    }

    default: {
      // Exhaustive check — TypeScript guards us, but a runtime fall-through
      // (e.g. a future server event we don't know about) should be quiet,
      // not loud.
      const _exhaustive: never = event;
      void _exhaustive;
      return;
    }
  }
}

/**
 * Reset every realtime-fed cache after a `replay.gap` notice. The server
 * could not replay everything we missed, so we drop the messages caches and
 * the conversations-list caches and let react-query refetch them — the next
 * render kicks off the fetches automatically because the observers are
 * still mounted.
 *
 * Lock cache slots are NOT cleared — they're cheap to be wrong about for
 * the brief window between refetch start and finish (a 5-minute soft lock
 * tolerates a few hundred milliseconds of staleness), and the user's
 * next mutation refreshes them anyway.
 *
 * The accounts list IS refetched: it is realtime-fed too (`account.status`),
 * and since 2026-09 its `attention` can change with NO state change
 * (time-based `connecting_stuck`) — a missed event must not leave a stale
 * warning banner (or a missing one) until the 30s poll.
 */
export function resetRealtimeFedCaches(queryClient: QueryClient): void {
  // Drop every messages cache slot — there's no batch refetch hook; the
  // next time `useMessages` mounts (e.g. user clicks a conversation) it
  // does a fresh GET. For the currently-mounted ChatPanel, we issue an
  // invalidate to trigger an immediate refetch instead of a slot drop.
  queryClient.invalidateQueries({ queryKey: chatKeys.messagesRoot() });
  // Merged-thread windows are realtime-fed the same way (message.new is
  // patched into the open thread's slot) — a gap means they may have missed
  // events too, so refetch them alongside the per-conversation windows.
  queryClient.invalidateQueries({ queryKey: chatKeys.threadMessagesRoot() });
  // Force a fresh list — invalidate fires every observer.
  queryClient.invalidateQueries({ queryKey: conversationsKeys.lists() });
  // Account status / detail / attention may have changed during the gap.
  queryClient.invalidateQueries({ queryKey: accountsQueryKey });
}

// Internal exports for tests. Not part of the public surface but exported
// so the projection test file can call them directly without round-tripping
// through `projectEvent` and constructing the wrapper event object.
export const __testing = {
  projectMessageNewIntoLists,
  projectMessageStatusIntoChat,
  projectConversationLock,
  messagePreview,
  /** Clear the cross-test seq-dedup memory. Tests that fire `projectEvent`
   *  must reset this in `beforeEach` so a seq from a prior test doesn't get
   *  swallowed as a duplicate. */
  resetSeenSeqs: (): void => {
    SEEN_SEQS.clear();
  },
  /** Clear the viewed-conversation auto-read debounce ledger AND cancel any
   *  pending trailing POST — same cross-test hygiene as `resetSeenSeqs`
   *  (real-clock debounce state would otherwise swallow a POST fired by a
   *  previous test's event, or leak a trailing POST into a later test). */
  resetViewedReadDebounce: (): void => {
    for (const timer of VIEWED_READ_PENDING_TRAILING.values()) {
      clearTimeout(timer);
    }
    VIEWED_READ_PENDING_TRAILING.clear();
    VIEWED_READ_LAST_POST.clear();
  },
  /** Clear the per-key invalidation throttle ledger AND cancel any pending
   *  trailing invalidation — ditto. */
  resetInvalidationCoalesce: (): void => {
    for (const timer of PENDING_TRAILING_INVALIDATION.values()) {
      clearTimeout(timer);
    }
    PENDING_TRAILING_INVALIDATION.clear();
    LAST_INVALIDATED_AT.clear();
  },
};

// Re-export the type so consumers don't have to import from the wider
// chat.api module just to consume `PagedMessages` in tests. Kept tiny.
export type { PagedMessages };
